'use client'

import Image from 'next/image'
import { ou, ouNombre } from '@/lib/contenuEditable'
import GrandTitle from '@/components/GrandTitle'
import { palette, type Fond } from '@/lib/palette'

// Section "Nos partenaires" — ligne horizontale de logos sur fond noir.
// Tous les logos sont uniformisés en blanc via `filter: brightness(0) invert(1)`
// (les fichiers source restent inchangés).
//
// `mobileMaxHeight` : hauteur max sur mobile (<768px)
// `mobileMaxWidth`  : largeur max sur mobile — utile pour brider les logos
//                     typographiques très larges qui sinon dominent
//                     visuellement les emblèmes carrés.
const PARTNERS = [
  { name: 'Champagne Cheurlin', logo: '/demo/placeholder.svg', maxHeight: 68, mobileMaxHeight: 40, mobileMaxWidth: 120 },
  { name: 'Liu Lisi', logo: '/demo/placeholder.svg', maxHeight: 60, mobileMaxHeight: 34, mobileMaxWidth: 110 },
  { name: 'Sicis', logo: '/demo/placeholder.svg', maxHeight: 56, mobileMaxHeight: 30, mobileMaxWidth: 120 },
]

/**
 * Contenu éditable depuis le CRM — section `partenaires` de la home.
 *
 * Les trois calibrages par logo ne sont pas exposés dans l'éditeur : ils
 * servent à brider les logos typographiques, très larges, pour qu'ils ne
 * dominent pas les emblèmes carrés. L'éditeur préserve ces clés quand on
 * modifie un nom ; un partenaire AJOUTÉ depuis l'éditeur n'en aura pas et
 * reçoit alors les valeurs de repli ci-dessous, sûres pour un logo carré.
 */
export interface PartnersContent {
  title?: string
  subtitle?: string
  items?: Array<{
    name?: string
    logo?: string
    maxHeight?: number
    mobileMaxHeight?: number
    mobileMaxWidth?: number
  }>
}

const CALIBRAGE_PAR_DEFAUT = { maxHeight: 96, mobileMaxHeight: 44, mobileMaxWidth: 100 }

export default function PartnersSection({ content, fond = 'dark' }: { content?: PartnersContent; fond?: Fond }) {
  const p = palette(fond)
  const titre = ou(content?.title, 'Nos partenaires')
  /* Une liste vide vaut « non renseigné » : on garde les partenaires
     d'origine plutôt que d'afficher une bande vide. */
  const partenaires =
    Array.isArray(content?.items) && content.items.length > 0
      ? content.items.map((it) => ({
          name: ou(it?.name, ''),
          logo: ou(it?.logo, ''),
          maxHeight: ouNombre(it?.maxHeight, CALIBRAGE_PAR_DEFAUT.maxHeight),
          mobileMaxHeight: ouNombre(it?.mobileMaxHeight, CALIBRAGE_PAR_DEFAUT.mobileMaxHeight),
          mobileMaxWidth: ouNombre(it?.mobileMaxWidth, CALIBRAGE_PAR_DEFAUT.mobileMaxWidth),
        })).filter((it) => it.logo !== '')
      : PARTNERS

  return (
    <section
      id="partenaires"
      style={{
        backgroundColor: p.fond,
        paddingTop: 'clamp(56px, 8vw, 110px)',
        paddingBottom: 'clamp(56px, 8vw, 110px)',
      }}
    >
      <style>{`
        /* Les logos sont ramenés à UNE couleur, celle qui se lit sur le fond :
           blanc sur l'encre, encre sur la crème. C'est le point sensible du
           passage en bicolore — un logo blanc sur fond crème disparaît. */
        #partenaires .demo-partner-logo {
          filter: ${p.sombre ? 'brightness(0) invert(1)' : 'brightness(0)'} !important;
          opacity: ${p.sombre ? 0.88 : 0.72};
        }
        /* Sur mobile uniquement, on réduit la taille des logos partenaires
           via les variables CSS --logo-mh-mobile / --logo-mw-mobile injectées
           inline par chaque logo, et on resserre le gap entre logos. */
        @media (max-width: 767px) {
          .demo-partners-row { gap: 28px 36px !important; }
          .demo-partner-logo {
            max-height: var(--logo-mh-mobile) !important;
            max-width: var(--logo-mw-mobile, 100%) !important;
          }
        }
      `}</style>
      <div className="max-w-6xl mx-auto px-6 md:px-8 text-center">
        {/* Structure du concept : kick + filet doré + grand titre Italiana. */}
        <p
          className="uppercase"
          style={{
            fontFamily: 'var(--font-body, inherit)',
            fontSize: 'clamp(11px, 0.6875rem + 0.15vw, 13px)',
            fontWeight: 600,
            letterSpacing: '0.3em',
            color: p.accent,
          }}
        >
          {titre}
        </p>
        <div style={{ width: 46, height: 1, backgroundColor: p.accent, margin: '22px auto' }} aria-hidden />
        <GrandTitle dark={p.sombre} style={{ marginBottom: 'clamp(40px, 5vw, 64px)' }}>
          {ou(content?.subtitle, 'Celles et ceux qui rendent l’impossible possible')}
        </GrandTitle>

        {typeof (content as Record<string, unknown>)?.body === 'string' && ((content as Record<string, unknown>).body as string).trim() !== '' && (
          <p style={{ maxWidth: 720, margin: '-24px auto clamp(36px, 5vw, 56px)', fontSize: 16, lineHeight: 1.85, color: p.texte(0.78), whiteSpace: 'pre-line' }}>
            {(content as Record<string, unknown>).body as string}
          </p>
        )}

        <div
          className="demo-partners-row flex flex-wrap items-center justify-center"
          style={{ gap: 'clamp(40px, 5vw, 80px)' }}
        >
          {/* `partenaire` et non `p` : `p` est la palette du bloc. */}
          {partenaires.map((partenaire) => (
            <div
              key={partenaire.name}
              className="flex items-center justify-center"
              style={{
                opacity: 0.85,
                transition: 'opacity 0.4s ease, transform 0.4s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '1'
                e.currentTarget.style.transform = 'scale(1.06)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '0.85'
                e.currentTarget.style.transform = 'scale(1)'
              }}
            >
              <Image
                src={partenaire.logo}
                alt={partenaire.name}
                width={400}
                height={400}
                className="demo-partner-logo w-auto"
                style={{
                  height: 'auto',
                  maxHeight: partenaire.maxHeight,
                  maxWidth: '100%',
                  objectFit: 'contain',
                  // Logos tous détourés (fond transparent) → aucun blend/box :
                  // grayscale pour un rendu sobre et uniforme, couleur au survol.
                  filter: 'grayscale(1)',
                  ['--logo-mh-mobile' as string]: `${partenaire.mobileMaxHeight}px`,
                  ['--logo-mw-mobile' as string]: `${partenaire.mobileMaxWidth}px`,
                } as React.CSSProperties}
              />
            </div>
          ))}
        </div>

        {(() => {
          const c = content as Record<string, unknown>
          const p1 = c?.primaryButton as { label?: string; href?: string } | undefined
          const p2 = c?.secondaryButton as { label?: string; href?: string } | undefined
          if (!p1?.label && !p2?.label) return null
          return (
            <div className="demo-partners-cta" style={{ marginTop: 'clamp(44px, 6vw, 72px)', display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
              {/* MOBILE : les 2 boutons sur une même ligne (partenaire gauche, mécène droite), rétrécis pour tenir */}
              <style dangerouslySetInnerHTML={{ __html: `
                @media (max-width: 767px) {
                  .demo-partners-cta { flex-wrap: nowrap !important; gap: 10px !important; }
                  .demo-partners-cta .gde-btn { flex: 1 1 0; min-width: 0; padding: 11px 8px !important; font-size: 11px !important; letter-spacing: 0.06em !important; white-space: nowrap; text-align: center; }
                }
              ` }} />
              {p1?.label && <a href={p1.href || '#'} className="gde-btn gde-btn--solid">{p1.label}</a>}
              {/* Le bouton secondaire est dessiné pour un fond sombre : sur la
                  crème, il reprend le bouton ordinaire. */}
              {p2?.label && <a href={p2.href || '#'} className={p.sombre ? 'gde-btn gde-btn--on-dark' : 'gde-btn'}>{p2.label}</a>}
            </div>
          )
        })()}
      </div>
    </section>
  )
}
